import { isNil } from 'lodash';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import { Box, Stack, Text } from '@atlaskit/primitives';
import { CreditCardPaymentMethod, PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import {
  FormattedAddress,
  FormattedDate,
  SummaryPanelContainer,
  SummaryPanelIcon,
  SummaryPanelRow,
  useFeatureToggle,
  ContactsTooltipType,
  ContactsTooltip,
  CopyableText,
  CustomPanel,
  ShowMoreLess,
} from '@css-xp/base';
import { useTransactionAccountContext } from '@css-xp/base/common-context';
import { ExtendedInvoicePPC } from '@css-xp/billing-v2';
import {
  InvoicePaymentMethod,
  Maybe,
  PaymentMethodType,
  Scalars,
  SourceSystem,
  TransactionAccount,
  InvoiceLineItems,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { BillingContactAddress, BillingDocWithLineItems } from '@atlassian/cssxp-types-billing';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { NoData, SoldToAddressDetailsLink } from '@css-xp/ppc-common';
import { TaxComponent } from '@css-xp/ppc-tax-component';
import { TermPaymentMethod } from '@css-xp/base/term-payment-method';
import { routePaths, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { EndCustomerContacts } from '@css-xp/contact-resources';
import { getRouteUrl, isPaypalPaymentMethod } from '@css-xp/ppc-utils';
import { useOrderNumberData } from '../../hooks/use-order-number-data';
import { useQuoteNumberData } from '../../hooks/use-quote-number-data';
import { BillingProfileNameFromId } from '@css-xp/ppc-common/src/billing-profile-name-from-id';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import './invoice-details-summary-section.scss';

type ExtendedBillingDocWithLineItems = BillingDocWithLineItems & {
  pricingCurrency: string | null;
};

function getFormattedAddress(
  address: BillingContactAddress,
  fullName: string | null,
  shouldHideOrgNameInAddressComponent = false
) {
  return (
    <FormattedAddress
      addressLine1={address?.address1}
      addressLine2={address?.address2}
      city={address?.city}
      country={address?.country}
      postcode={address?.postalCode}
      state={address?.state}
      organizationName={shouldHideOrgNameInAddressComponent ? null : fullName}
    />
  );
}

export const getSoldToAddress = (
  billingDoc: ExtendedBillingDocWithLineItems,
  transactionAccountId?: string,
  hasLineClamp?: boolean,
  shouldHideOrgNameInAddressComponent?: boolean
) => {
  const shipToId = get(billingDoc, 'shipTo.id', null);
  if (shipToId) {
    return (
      <div>
        <SoldToAddressDetailsLink
          txaId={transactionAccountId}
          shipToId={shipToId}
          shouldRemoveLineClamp={!hasLineClamp}
        >
          <div className="customer-address-section">
            {shouldHideOrgNameInAddressComponent && (
              <Text as="p" weight="bold" testId="organisation-name">
                {billingDoc?.shipTo?.fullName}
              </Text>
            )}
            <span className={hasLineClamp ? 'line-clamp' : ''}>
              {billingDoc?.shipTo?.address?.address1 ? (
                getFormattedAddress(
                  billingDoc?.shipTo?.address,
                  billingDoc?.shipTo?.fullName,
                  shouldHideOrgNameInAddressComponent
                )
              ) : (
                <p>Same as billing address</p>
              )}
            </span>
          </div>
        </SoldToAddressDetailsLink>
        <TaxComponent
          billToAddress={billingDoc?.shipTo}
          gridItemClassName="tax-component-section"
          hasSeparator={true}
        />
      </div>
    );
  }
  return <p>Same as billing address</p>;
};

type ExtendedInvoice = ExtendedInvoicePPC & {
  totalCreditLimit?: Maybe<Scalars['Float']>;
  onTermPaymentMethod?: Maybe<Scalars['String']>;
};

const getPaidLabel = (billingDoc: ExtendedInvoicePPC) => {
  if (
    billingDoc.paymentMethod &&
    // leaving as array in case more items needed to be added
    billingDoc.paymentMethod in InvoicePaymentMethod
  ) {
    if (billingDoc.status && ['PAID'].includes(billingDoc.status)) {
      return 'Paid on';
    }
    return 'Due by';
  }
  return 'Due by';
};

export const getPaymentMethodSection = ({
  invoice,
  hasPayByStatement,
  termPaymentType,
  modifiedInvoice,
  termPaymentStatus,
  isPaypalEnabled = false,
}: {
  invoice?: ExtendedInvoicePPC;
  hasPayByStatement?: boolean;
  termPaymentType?: Maybe<string>;
  modifiedInvoice?: ExtendedInvoice;
  termPaymentStatus?: Maybe<string>;
  isPaypalEnabled?: boolean;
}) => {
  if (isPaypalEnabled && isPaypalPaymentMethod(invoice?.paymentMethod)) {
    return <PaypalPaymentMethod emailID={invoice?.paypalAccountEmail} />;
  }
  if (invoice?.paymentMethod === InvoicePaymentMethod.DEFERRED) {
    return (
      <TermPaymentMethod
        paymentNetType={hasPayByStatement ? '' : (termPaymentType as string)}
        totalLimit={hasPayByStatement ? undefined : (modifiedInvoice?.totalCreditLimit as number)}
        currentTermAccountStatus={termPaymentStatus}
        hasPayOnAccount={!!hasPayByStatement}
      />
    );
  }

  if (invoice?.creditCard) {
    return (
      <PaymentMethodPanel
        paymentMethod={
          {
            id: invoice?.paymentMethodId as PaymentMethodId,
            type: PaymentMethodType.CARD,
            card: {
              brand: invoice?.creditCard?.brand ?? '',
              expMonth: +(invoice?.creditCard?.expirationMonth ?? 0),
              expYear: +(invoice?.creditCard?.expirationYear ?? 0),
              last4: invoice?.creditCard?.last4Numbers ?? '',
              name: invoice?.creditCard?.cardHolderName ?? '',
            },
          } as CreditCardPaymentMethod
        }
      />
    );
  }
};

export const InvoiceDetailSummarySection = ({
  billingDoc,
  transactionAccountId,
  shouldSkipFetchInHooks,
  hasPayByStatement,
  isQuoteAndOrderNumbersEnabled,
  invoiceGroupId,
  isDesignIteration1Enabled,
  isPaypalEnabled,
}: {
  billingDoc?: ExtendedBillingDocWithLineItems;
  transactionAccountId?: string;
  /* shouldSkipFetchInHooks is used to skip the fetch in the useOrderNumberData and useQuoteNumberData hooks */
  shouldSkipFetchInHooks?: boolean;
  hasPayByStatement?: boolean;
  isQuoteAndOrderNumbersEnabled?: boolean;
  invoiceGroupId?: string;
  isDesignIteration1Enabled?: boolean;
  isPaypalEnabled?: boolean;
}) => {
  const [isEnabled] = useFeatureToggle();
  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = getTransactionAccount(transactionAccountData) as TransactionAccount;

  const termPaymentStatus = useMemo(() => {
    const termPaymentStatus = transactionAccount?.billingAccountsList?.status;

    return termPaymentStatus;
  }, [transactionAccount]);
  const invoice = billingDoc as ExtendedInvoicePPC;
  const paymentReferenceObj = invoice?.userDefinedNotes;
  const renderPaymentReference = useMemo(() => {
    const referenceIds = paymentReferenceObj && Object.keys(paymentReferenceObj);
    return <SummaryPanelRow label={'Payment Reference'} value={referenceIds?.join(', ') ?? <NoData />} />;
  }, [paymentReferenceObj]);

  const orderLineIds = (billingDoc as any)?.lineItems?.map((lineItem: InvoiceLineItems) => lineItem?.orderLineId);
  const { data: orderNumberData, loading: orderNumberDataLoading } = useOrderNumberData({
    txaId: transactionAccountId ?? '',
    orderLineIds: orderLineIds,
    shouldSkipFetch: shouldSkipFetchInHooks,
  });

  const { data: quoteNumberData, loading: quoteNumberDataLoading } = useQuoteNumberData({
    orderLineIds: orderLineIds,
    shouldSkipFetch: shouldSkipFetchInHooks,
    transactionAccountId: transactionAccountId,
  });

  if (!billingDoc) {
    return null;
  }

  const renderQuoteLink = (quoteId: string, quoteNumber: string) => {
    const getRoute = () => {
      const route =
        transactionAccountId &&
        getRouteUrl(routePaths.ppcQuoteDetails, { txaId: transactionAccountId, quoteId: quoteId });

      return route;
    };

    return <Link to={`${getRoute()}`}>{quoteNumber}</Link>;
  };

  const renderOrderLink = (orderId: string, orderNumber: string) => {
    const getRoute = () => {
      const route =
        transactionAccountId && getRouteUrl(routePaths.orderDetails, { txaId: transactionAccountId, orderId: orderId });

      return route;
    };

    return <Link to={`${getRoute()}`}>{orderNumber}</Link>;
  };

  const renderQuoteNumberRow = () => {
    let quoteNumber;

    if (quoteNumberDataLoading) {
      quoteNumber = (
        <SkeletonLoaderWrapper testId="quote-number-data-loading">
          <SkeletonLoader width={isDesignIteration1Enabled ? '200px' : ''} />
        </SkeletonLoaderWrapper>
      );
    }

    const quotes = quoteNumberData?.quotes;

    if (quotes?.totalCount) {
      quoteNumber = quotes?.nodes?.map((quote: any, index: number) => {
        return (
          <React.Fragment key={index}>
            {index > 0 && ', '}
            {renderQuoteLink(quote?.id, quote?.quoteNumber)}
          </React.Fragment>
        );
      });
    }

    return (
      <SummaryPanelRow
        label={'Quote number'}
        value={quoteNumber ?? <NoData />}
        useWrapper={isDesignIteration1Enabled}
      />
    );
  };

  const renderOrderNumberRow = () => {
    let orderNumber;

    if (orderNumberDataLoading) {
      orderNumber = (
        <SkeletonLoaderWrapper testId="order-number-data-loading">
          <SkeletonLoader width={isDesignIteration1Enabled ? '200px' : ''} />
        </SkeletonLoaderWrapper>
      );
    }

    const orders = orderNumberData?.transactionAccount?.orders;
    const ordersCount = orders?.totalCount;

    if (ordersCount) {
      if (isDesignIteration1Enabled) {
        const items = orders?.nodes?.map((order: any, index: number) => {
          return (
            <Stack key={index} space="space.050">
              {renderOrderLink(order?.id, order?.orderNumber)}
            </Stack>
          );
        });
        orderNumber = <ShowMoreLess items={items} itemsToShow={4} />;
      } else {
        orderNumber = orders?.nodes?.map((order: any, index: number) => {
          return (
            <React.Fragment key={index}>
              {index > 0 && ', '}
              {renderOrderLink(order?.id, order?.orderNumber)}
            </React.Fragment>
          );
        });
      }
    }

    return (
      <SummaryPanelRow
        label={ordersCount === 1 ? 'Order number' : 'Order numbers'}
        value={orderNumber ?? <NoData />}
        useWrapper={isDesignIteration1Enabled}
      />
    );
  };

  const renderInvoiceIdRow = () => {
    return <SummaryPanelRow label="Invoice ID" value={getInvoiceId(invoice)} useWrapper={isDesignIteration1Enabled} />;
  };

  const renderPONumberRow = () => {
    if (invoice.sourceSystem !== SourceSystem.CCP) return null;

    return (
      <SummaryPanelRow
        label="PO number"
        value={!isNil(invoice.purchaseOrderNumber) ? invoice?.purchaseOrderNumber : <NoData />}
        useWrapper={isDesignIteration1Enabled}
      />
    );
  };

  /**
   *  if the payment method is deferred, totalCreditLimit and onTermPaymentMethod are added as two extra fields
   *  to the data coming from the hydra in the invoice-details-page
   */
  const modifiedInvoice = billingDoc as ExtendedInvoice;
  const getInvoiceId = (invoice: ExtendedInvoicePPC) => {
    return (
      <div>
        <div>{invoice.id && <CopyableText text={invoice.id} />}</div>
      </div>
    );
  };

  const termPaymentType = modifiedInvoice?.onTermPaymentMethod;

  if (isDesignIteration1Enabled) {
    const renderMemoRow = () => {
      return <SummaryPanelRow label="Memo" value={invoice?.memo || <NoData />} useWrapper={true} />;
    };

    return (
      <Box testId="invoice-details-summary-section-v2">
        <CustomPanel expandedHeader="Invoice details" collapsedHeader="Invoice details" version={2} isExpanded>
          <Box padding="space.250" paddingBlockStart="space.150">
            <SummaryPanelContainer version={2}>
              {renderInvoiceIdRow()}
              {isQuoteAndOrderNumbersEnabled && renderQuoteNumberRow()}
              {renderPONumberRow()}
              {isQuoteAndOrderNumbersEnabled && renderOrderNumberRow()}
              {renderMemoRow()}
            </SummaryPanelContainer>
          </Box>
        </CustomPanel>
      </Box>
    );
  }

  return (
    <div className="invoice-details-summary-section">
      <div className="invoice-summary-section">
        <SummaryPanelContainer title="Invoice details" iconKey={SummaryPanelIcon.SUBSCRIPTION_ICON}>
          {renderInvoiceIdRow()}
          <SummaryPanelRow
            label="Bill-to address"
            value={
              <div className="customer-address-section">
                <span className="line-clamp">
                  {billingDoc?.billTo?.address?.address1 ? (
                    getFormattedAddress(billingDoc?.billTo?.address, billingDoc?.billTo?.fullName)
                  ) : (
                    <NoData />
                  )}
                </span>
                <TaxComponent
                  billToAddress={billingDoc?.billTo}
                  gridItemClassName="tax-component-section"
                  hasSeparator={true}
                />
              </div>
            }
          />
          <SummaryPanelRow label="Sold-to address" value={getSoldToAddress(billingDoc, transactionAccountId, true)} />
        </SummaryPanelContainer>
      </div>
      <div className="invoice-summary-section">
        <SummaryPanelContainer title="Billing and payment details" iconKey={SummaryPanelIcon.BILLING_PAYMENT_ICON}>
          <SummaryPanelRow
            label="Issued on"
            value={<FormattedDate date={invoice?.createdTimestamp} showTime={true} format={NEW_DATE_FORMAT} />}
          />
          {!hasPayByStatement && (
            <SummaryPanelRow
              label={getPaidLabel(billingDoc as ExtendedInvoicePPC)}
              value={<FormattedDate date={invoice.dueDate} format={NEW_DATE_FORMAT} />}
            />
          )}
          {isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_LINK) && (
            <SummaryPanelRow
              label="Billing Profile"
              value={<BillingProfileNameFromId id={invoiceGroupId} txaId={transactionAccountId || ''} />}
            />
          )}
          <SummaryPanelRow
            testId="invoice-payment-method"
            label={'Payment method'}
            value={
              getPaymentMethodSection({
                invoice,
                hasPayByStatement,
                termPaymentType,
                modifiedInvoice,
                termPaymentStatus,
                isPaypalEnabled,
              }) ?? <NoData />
            }
          />
          <SummaryPanelRow
            label={'Currency'}
            value={(invoice?.pricingCurrency || invoice?.paymentCurrency) ?? <NoData />}
          />

          {renderPONumberRow()}
          {isQuoteAndOrderNumbersEnabled ? (
            <>
              {renderQuoteNumberRow()}
              {renderOrderNumberRow()}
            </>
          ) : null}
          {renderPaymentReference}
        </SummaryPanelContainer>
      </div>
      {invoiceGroupId && transactionAccountId && (
        <div className="invoice-summary-section">
          <SummaryPanelContainer title="Customer contacts" iconKey={SummaryPanelIcon.AVATAR_ICON}>
            <SummaryPanelRow
              label={
                <ContactsTooltip type={ContactsTooltipType.CUSTOMER} iconSize="small">
                  Customer contacts
                </ContactsTooltip>
              }
              value={
                <EndCustomerContacts invoiceGroupId={invoiceGroupId} transactionAccountId={transactionAccountId} />
              }
            />
          </SummaryPanelContainer>
        </div>
      )}
    </div>
  );
};
